import React from "react";
import { useTranslation } from "react-i18next";
import { StyledModal, StyledLabel } from "@aureskonnect/react-ui";
import { ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useSnapshot } from "valtio";
import { toast } from "react-toastify";
import { uuid } from "uuidv4";
import {
    store as generalSettingStore,
    setIsValidateArchiveOpened,
    setIsValidateShopArchiveOpened,
    setIsLoading,
    setKey,
} from "./store";
import { store } from "@components/VerticalLayout/store";
import { generalConfigStore, setItemsDisable } from "@store";
import { capitalize } from "@helpers/general";
import { CustomMainColorButton } from "@components/Common/CustomMainColorButton";
import { CustomSecondaryColorButton } from "@components/Common/CustomSecondaryColorButton";
import ErrorToast from "@components/Common/ErrorTost";
import {
    apiRequest,
    DataConsumed,
    getArrayOfRows,
    processRows,
    processRowsShops,
} from "./hooks";
import { ShowSuccessToast } from "./ShowSuccessToast";
import { useFetchGlobal } from "../../../api/hooks";

type ConfirmationArchiveLanguageType = {
    selectedRows: any;
    setLimit: Function;
};
export function ConfirmationArchiveLanguage({
    selectedRows,
    setLimit,
}: ConfirmationArchiveLanguageType) {
    const { fetchGlobal } = useFetchGlobal();
    const { t } = useTranslation();
    const {
        isValidateArchiveOpened,
        isValidateShopArchiveOpened,
    } = useSnapshot(generalSettingStore);
    const { userID, franchiseID, shopID, operatorID } = useSnapshot(store);
    const { itemsDisable } = useSnapshot(generalConfigStore);
    const uuidUser = localStorage.getItem("uuidUser");

    async function archiveFranchiseLanguage() {
        setIsLoading(true);
        toast.dismiss();
        setLimit(3);
        let shopIds: any = [];
        let idShops: any = [];
        let languageData: any = {};
        selectedRows.forEach((el: any) => {
            if (!shopIds.includes(el.shopId)) {
                shopIds.push(el.shopId);
            }
        });
        const queryParam = encodeURIComponent(JSON.stringify(shopIds));
        let dataConsumed: any = await DataConsumed(
            queryParam,
            t,
            franchiseID,
            fetchGlobal
        );
        let { isSomeConsumed, isFavorite } = processRowsShops(
            selectedRows,
            dataConsumed,
            idShops,
            languageData
        );

        if (Object.keys(languageData).length !== 0) {
            let dataLanguage = {
                userId: userID,
                shopId: [shopID],
                franchiseId: franchiseID,
                operatorId: operatorID,
                uuidUser: uuidUser,
                isShop: false,
                data: languageData,
                isMultiShop: true,
            };

            try {
                const result: any = await apiRequest(dataLanguage, fetchGlobal);
                if (result.error) {
                    throw new Error("error");
                }
                ShowSuccessToast("The language was successfully archived");
                setIsLoading(false);
                setKey(uuid());
            } catch (e) {
                ErrorToast(`${t("There's an error")}`);
            }
        }
        if (isSomeConsumed) {
            ErrorToast(
                t(
                    "Please note, you cannot archive an element used in a template"
                )
            );
            setIsLoading(false);
        }
        if (isFavorite) {
            ErrorToast(
                t("Please note, you cannot archive a favorite language")
            );
            setIsLoading(false);
        }
    }
    async function archiveShopLanguage() {
        setIsLoading(true);
        toast.dismiss();
        setLimit(3);

        let arrayOfRows = getArrayOfRows({ selectedRows }, t);

        let languageData: any = {};
        let shopIds: any = [];
        const idShops = [...new Set(arrayOfRows.map((el: any) => el.shopId))];
        const queryParam = encodeURIComponent(JSON.stringify(idShops));
        let dataConsumed: any = await DataConsumed(
            queryParam,
            t,
            franchiseID,
            fetchGlobal
        );

        const result = processRows(
            arrayOfRows,
            dataConsumed,
            shopIds,
            languageData,
            itemsDisable,
            setItemsDisable
        );

        let dataLanguage = {
            userId: userID,
            shopId: shopIds,
            franchiseId: franchiseID,
            operatorId: operatorID,
            uuidUser: uuidUser,
            isShop: true,
            data: languageData,
            isMultiShop: true,
        };
        if (Object.keys(languageData).length > 0) {
            try {
                const result: any = await apiRequest(dataLanguage, fetchGlobal);
                if (result.error) {
                    throw new Error("error");
                }
                ShowSuccessToast(
                    `${t("The language was successfully archived")}`
                );
                setKey(uuid());
                setIsLoading(false);
            } catch (e) {
                ErrorToast(`${t("There's an error")}`);
                setIsLoading(false);
            }
        }

        if (result.isSomeFavorite === true) {
            ErrorToast(
                t("Please note, you cannot archive a favorite language")
            );
            setIsLoading(false);
        }
        if (result.isSomeConsumed === true) {
            ErrorToast(
                t(
                    "Please note, you cannot archive an element used in a template"
                )
            );
            setIsLoading(false);
        }
    }
    return (
        <StyledModal
            toggle={true}
            isOpen={isValidateArchiveOpened || isValidateShopArchiveOpened}
            centered
        >
            <ModalHeader
                className="text-capitalize"
                toggle={() => {
                    setIsValidateArchiveOpened(false);
                    setIsValidateShopArchiveOpened(false);
                }}
            >
                <span>{capitalize(t("Alert"))}</span>
            </ModalHeader>
            <ModalBody>
                <StyledLabel
                    className="pl-3 m-0"
                    style={{ whiteSpace: "pre-line" }}
                >
                    {t(
                        "Attention, these language(s) can be used by other modules.\n Do you want to confirm the archiving of the selected language(s)"
                    )}
                </StyledLabel>
            </ModalBody>
            <ModalFooter className="border-top-0 ">
                <React.Fragment>
                    <CustomSecondaryColorButton
                        outline
                        variant="light"
                        onClick={() => {
                            setIsValidateArchiveOpened(false);
                            setIsValidateShopArchiveOpened(false);
                        }}
                        rounded
                    >
                        {t("Cancel")}
                    </CustomSecondaryColorButton>
                    <CustomMainColorButton
                        rounded
                        className="mr-2"
                        variant="primary"
                        onClick={() => {
                            isValidateArchiveOpened
                                ? archiveFranchiseLanguage()
                                : archiveShopLanguage();
                            setIsValidateArchiveOpened(false);
                            setIsValidateShopArchiveOpened(false);
                        }}
                    >
                        {t("Validate")}
                    </CustomMainColorButton>
                </React.Fragment>
            </ModalFooter>
        </StyledModal>
    );
}
